import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { openSignedInTap } from '../../utils/openDocument';
import type { DashboardDeadline, DeadlineDocument } from '../../types';

interface Props {
  deadline: DashboardDeadline;
  onClose: () => void;
  onChanged: () => void;
}

export default function DeadlineDocsPopover({ deadline, onClose, onChanged }: Props) {
  const [docs, setDocs] = useState<DeadlineDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [autoMsg, setAutoMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setDocs((await api.listDeadlineDocuments(deadline.id)).documents || []); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load documents'); }
  }, [deadline.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true); setErr(null);
    let completed = false;
    try {
      for (const file of files) {
        const res = await api.attachDeadlineDocument(deadline.id, deadline.deal_id, file);
        if (res?.deadline_completed) completed = true;
      }
      await load(); onChanged();
      if (completed) setAutoMsg(`Marked "${deadline.label}" complete.`);
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Upload failed'); }
    finally { setBusy(false); }
  };
  const undoComplete = async () => {
    await api.updateDealDeadline(deadline.id, { is_completed: false });
    setAutoMsg(null);
    onChanged();
  };
  const importUrl = async () => {
    if (!url) return;
    setBusy(true); setErr(null);
    try { await api.importDeadlineDocumentFromUrl(deadline.id, url); setUrl(''); await load(); onChanged(); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Import failed'); }
    finally { setBusy(false); }
  };
  const open = async (doc: DeadlineDocument) => {
    // MOB4: the window opens INSIDE the tap, then is pointed at the signed link (utils/openDocument).
    try { await openSignedInTap(() => api.getDeadlineDocumentDownloadUrl(deadline.id, doc.id).then((res) => res.url)); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed to open'); }
  };
  const detach = async (doc: DeadlineDocument) => {
    try { await api.detachDeadlineDocument(deadline.id, doc.id); await load(); onChanged(); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed to detach'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b">
          <h3 className="font-semibold text-gray-900">{deadline.label}</h3>
          <p className="text-xs text-gray-500">{deadline.deal_name}</p>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="text-sm text-red-600">{err}</div>}
          {autoMsg && <div className="text-sm text-success flex items-center gap-2">{autoMsg}<button onClick={undoComplete} className="text-primary-500 underline">Undo</button></div>}
          {docs.length === 0 && <p className="text-sm text-gray-400">No documents attached.</p>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm">
              <button onClick={() => open(d)} className="flex-1 text-left text-primary-500 hover:underline truncate">{d.file_name}</button>
              <button onClick={() => detach(d)} className="text-gray-300 hover:text-red-500 text-xs">Detach</button>
            </div>
          ))}
          <div className="pt-3 border-t space-y-2">
            <label className="block">
              <span className="text-xs text-gray-500">Upload a file</span>
              <input type="file" multiple onChange={upload} disabled={busy} className="block w-full text-sm mt-1" />
            </label>
            <div className="flex gap-2">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a Dropbox / Google Drive link"
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
              <button onClick={importUrl} disabled={busy || !url} className="px-3 py-1 bg-primary-500 text-white rounded text-sm disabled:opacity-50">Import</button>
            </div>
          </div>
        </div>
        <div className="p-3 border-t flex justify-end">
          <button onClick={onClose} className="px-3 py-1 border rounded text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}
